/**
 * `/about-us/careers/`: the Ryerson Careers landing page. On any other `NEXT_PUBLIC_COMPANY` the
 * page isn't built; the Southern Tool Steel counterpart is
 * `src/pages/who-we-are/careers/index.tsx`. The careers sub-pages live in the folders beside
 * this file.
 *
 * Top to bottom: the hero (`components/GrowWithSales/Hero/Hero.tsx` with `isCareers`, so it
 * carries the job-board buttons from `components/Shared/CareersButtons/CareersButtons.tsx`,
 * which open `NEXT_PUBLIC_CAREERS_URL_USA` and `NEXT_PUBLIC_CAREERS_URL_CANADA`), then one
 * `components/Shared/AllSections/AllSections.tsx` list:
 * - Ignite Your Career With Ryerson (`components/Careers/IgniteYourCareer/IgniteYourCareer.tsx`),
 *   cards linking to the four sub-pages;
 * - In Our DNA (`components/Shared/InOurDNA/InOurDNA.tsx`);
 * - Benefits of Joining Our Team (`components/WorkingAtRyerson/Benefits/Benefits.tsx`);
 * - Hear From Our People (`components/Careers/HearFromOurPeople/HearFromOurPeople.tsx`).
 * An Awards section is commented out here, as on `src/pages/about-us/index.tsx`, so
 * `components/Careers/Awards/Awards.tsx` has no live caller.
 *
 * Copy comes from `src/content/ryerson/about-us/careers/index.ts` (SEO, hero, benefits), whose
 * `PageContent` is the page's site-search entry, and from each section's `Content.tsx`. Section
 * titles are the anchors `AllSections` builds, so changing a title changes the page's deep-link
 * ids. `src/unit-test/AboutUs/CareersVariant.spec.tsx` renders a careers sub-page, not this one.
 */
import React from "react";
import { AllSections, Sections } from "@components/Shared/AllSections/AllSections";
import { useApplication } from "@ryerson/frontend.application";
import InOurDNA from "@components/Shared/InOurDNA/InOurDNA";
import { InOurDNAContent } from "@components/Shared/InOurDNA/Content";
import CareersHero from "@components/GrowWithSales/Hero/Hero";
import IgniteYourCareer from "@components/Careers/IgniteYourCareer/IgniteYourCareer";
import { IgniteYourContent, LanguageParallax } from "@components/Careers/IgniteYourCareer/Content";
import Benefits from "@components/WorkingAtRyerson/Benefits/Benefits";
import { HearFromOurContent } from "@components/Careers/HearFromOurPeople/Content";
import HearFromOurPeople from "@components/Careers/HearFromOurPeople/HearFromOurPeople";
import { useTheme } from "@ryerson/frontend.theme";
import MetaHead from "@components/Shared/model/MetaHead";
import { JoinBenefitsContent, HeroContent, MetaObject } from "@content/ryerson/about-us/careers";
import { allContentfulHeaderCalls, HeaderPageProps } from "@ryerson/frontend.contentful";
import HeaderLayout from "@components/NextLayouts/HeaderLayout";
import { getHeaderData } from "@components/NextLayouts/nextUtilities";

/** Ryerson build only. Fetches the header and footer data at build time. */
export const getStaticProps = async () => {
	if (process.env.NEXT_PUBLIC_COMPANY !== "ryerson") {
		return { notFound: true }; // tells Next.js to skip page generation and return 404
	}

	return await allContentfulHeaderCalls();
};

/** Renders the Careers page in the current language; see the file header for the layout. */
const Page: React.FC<HeaderPageProps> = (props) => {
	const {
		localization: { language },
	} = useApplication();
	const { theme } = useTheme();

	// Per-language placement of the Ignite section's rotated title words.
	const parallax = LanguageParallax[language];
	const sections: Sections[] = [];
	sections.push({
		title: IgniteYourContent[language].title1 + " " + IgniteYourContent[language].title2,
		cmp: (
			<IgniteYourCareer
				rightBackground={theme.colors.primary.darkerGray}
				title1Left={parallax.title1Left}
				title2Left={parallax.title2Left}
				title1LeftSmaller={parallax.title1LeftSmaller}
				title2LeftSmaller={parallax.title2LeftSmaller}
				title1Parallax={parallax.title1Parallax}
				title2Parallax={parallax.title2Parallax}
				title1ParallaxSmaller={parallax.title1ParallaxSmaller}
				title2ParallaxSmaller={parallax.title2ParallaxSmaller}
				customFontSize={parallax.customFontSize}
			/>
		),
		maxHeight: "5000px",
	});
	sections.push({
		title: InOurDNAContent[language].title,
		cmp: <InOurDNA background="primary" />,
	});
	sections.push({
		title: JoinBenefitsContent[language].title,
		cmp: <Benefits content={JoinBenefitsContent[language]} />,
	});
	sections.push({
		title: HearFromOurContent[language].title,
		cmp: <HearFromOurPeople content={HearFromOurContent[language]} />,
	});
	// sections.push({
	// 	title: AwardContent[language].title,
	// 	cmp: <Awards content={AwardContent[language]} />,
	// });
	return (
		<HeaderLayout {...getHeaderData(props)}>
			<MetaHead content={MetaObject[language]} />
			<CareersHero content={HeroContent[language]} isCareers={true} />
			<AllSections sections={sections} />
		</HeaderLayout>
	);
};

export default Page;
